import { updateModuleCache, cacheAllModuleFiles } from '@/store/sync/cachingUtils';
import { performInMemorySearch } from '@/components/search/searchStore';
import { SYNC_STATES } from '@/store/sync/sync';
import storeService from '@/services/storeService';
import fahrplanApi from './fahrplanApi';
import dataUtils from '@/common/dataUtils';
import moduleId from './moduleId';
import moduleIndex from './index';
import fahrplanUtil from './fahrplanUtil';
import favoriteService from '@/services/favoritesService';
import dateUtils from '@/common/dateUtils';
import readService from '@/services/readService';
import acknowledgeService from '../../services/acknowledgeService';

const ROOT = { root: true };

const state = {
  // Data - all other arrays are stored in non-reactive store (=object), which is faster because no watchers are getting attached
  // Fahrdienst_files comment:
  // Both fields "dienst" and "linie" contain comma separated list of fahrplan_attributes.value field (that is the name and not the id!!!!!)
  fahrplanAttributes: [],
  fahrplanFiles: [],
  fahrplanFilesById: {},
  userState: [],
  verkehrsPeriodenKalender: [],
  // View State
  // dutyFiles: [],
  displayedDocument: null,
  unreadCountsByInstance: {},

  isLoaded: false, // true indicates, data has been loaded (at least one sync process)
  selectedFile: null,
  selectedDate: new Date(),
  selectedType: null,
  selectedLine: null,
};

const getters = {
  //
  /** ------------------------------------------------------------------------------------------------------------------
   * Module Implementation for unread badge counts by instance.
   * IMPLEMENT this getter to enable unread counter badges
   * -------------------------------------------------------------------------------------------------------------------
   */
  getUnreadCountByModuleInstance: (state) => (instance) => {
    return 0;
    // return state.unreadCountsByInstance[instance];
  },

  getFilteredFahrdienstFiles: (state) => {
    let { selectedDate, selectedDuty, selectedLine } = state;

    let filtered = fahrplanUtil.filterByDutyLineAndDate({
      fahrplanFiles: state.files,
      instance: state.instance,
      selectedDateIsoString: selectedDate,
      selectedDuty,
      selectedLine,
    });
    // console.debug('filtered', JSON.stringify(filtered, null, 2));
    return filtered;
  },
  /**
   * Returns an array of unique lines among fahrplan files filtered by selectedDate, selectedDuty and selectedLine.
   */
  getLines: (state, getters) => {
    const { instance } = state;
    let uniqueLinesArray = [];
    if (instance) {
      const files = getters.getFilteredFahrdienstFiles;
      const uniqueLines = {};
      files.forEach((f) => {
        if (f.linie) {
          const lines = f.linie.split(',');
          lines.forEach((l) => (uniqueLines[l] = l));
        }
      });
      uniqueLinesArray = Object.keys(uniqueLines);
    }
    console.debug('uniqueLinesArray', uniqueLinesArray);
    return uniqueLinesArray;
  },
  /**
   * Returns an array of unique duties among fahrplan files filtered by selectedDate, selectedDuty and selectedLine.
   */
  getDuties: (state, getters) => {
    const { instance } = state;
    if (instance) {
      const files = getters.getFilteredFahrdienstFiles;
      const uniqueDuties = {};
      files.forEach((f) => {
        if (f.dienst) {
          const duties = f.dienst.split(',');
          duties.forEach((l) => (uniqueDuties[l] = l));
        }
      });
      return Object.keys(uniqueDuties);
    }
    return [];
  },

  dienstFilesByFileType: (state) => (fileTypeIdFk) => {
    return state.fahrplanFile.filter((f) => f.typeIdFk === fileTypeIdFk);
  },
};

const mutations = {
  setSelectedLine(state, line) {
    state.selectedLine = line;
  },
  setSelectedType(state, type) {
    state.selectedType = type;
  },
  setSelectedDayType(state, dayType) {
    state.selectedDayType = dayType;
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for UPDATING Module counters
   * ------------------------------------------------------------------------------------------------------------------ *
   */
  countModuleItems(state) {
    // count all unread files (not duty specific).
    // Not implemented in this module
  },
  setData(state, data) {
    state.fahrplanAttributes = data.fahrplanAttributes;
    state.fahrplanFilesById = data.fahrplanFilesById;
    state.fahrplanFiles = data.fahrplanFiles;
    state.userState = data.userState;
    state.verkehrsPeriodenKalender = data.verkehrsPeriodenKalender;
    state.isLoaded = true;
  },

  resetView: (state) => {
    state.displayedDocument = null;
  },
};

const actions = {
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for IN-MEMORY SEARCH
   * IMPLEMENT this action to enable module in-memory search
   * Call the action in search/searchStore (search for "Module in-memory searches" there)
   * Search all documents containing all of the space separated search words in state.filterCriteria
   * ------------------------------------------------------------------------------------------------------------------ *
   */
  searchModuleInMemoryContainingSearchTerm({ state }, { filterCriteria, options }) {
    return performInMemorySearch({
      moduleId,
      items: state.fahrplanFiles,
      titleSearchAttr: 'title',
      contentSearchAttr: null,
      searchCriteriaArray: filterCriteria,
      options,
    });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Updates the browsers cache storage oevpad-api/modules/{moduleId} with the exact same data structure as received by the module sync api call!
   * Reason: This mutation MUST be called whenever data has changed, e.g. user_state data. It assures that the application cache is up-to-date
   *         when the user synchronized/updates in offline mode.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async updateModuleApplicationCache({ state }) {
    const { fahrplanAttributes, fahrplanFiles, userState, verkehrsPeriodenKalender } = state;
    await updateModuleCache(moduleIndex.api, {
      fahrplanAttributes,
      fahrplanFiles,
      userState,
      verkehrsPeriodenKalender,
    });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   * Synchronize module files, that is storing files into browsers application cache by simply loading files from server.
   * The service-worker will intercept all requests and put files into cache if not yet there.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async synchronizeFiles({ commit, dispatch, state }) {
    await cacheAllModuleFiles({ files: state.fahrplanFiles, moduleIndex, commit, dispatch });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   * Synchronize module files, that is storing files into browsers application cache by simply loading files from server.
   * The service-worker will intercept all requests and put files into cache if not yet there.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async synchronizeData({ commit, dispatch, rootState, rootGetters }) {
    try {
      const response = await fahrplanApi.getData();
      let { fahrplanAttributes, fahrplanFiles, userState, verkehrsPeriodenKalender } = response.data;
      // create helper objects (by id or the like)
      const fahrplanFilesById = {};
      const userStateByFahrdienstFileId = dataUtils.buildIdMap(userState, 'fileIdFk');

      let today = new Date();
      let dateYYYYMMDD = dateUtils.formatDateAsYYYYMMDD(today);
      fahrplanFiles.forEach((f) => {
        fahrplanFilesById[f.id] = f;
        //enhance item with information used when searching
        let moduleConfig = rootGetters['configs/getModuleConfig']({
          moduleType: moduleIndex.moduleType,
          instance: f.inst,
        });

        // add calculated data used mainly for displaying in live-search
        f._displayLocation = '';
        f._moduleTitle = moduleConfig.title;
        f._moduleId = moduleId;
        ///fahrplan/:instance/:dateYYYYMMDD/:documentId/docOnly
        // /fahrplan/:instance/:dateYYYYMMDD/:dutyId'
        //ocalhost:5171/fahrplan/1/2022-07-09/7350/docOnly?page=1&search
        f._path = `/fahrplan/${f.instance}/${f.id}`; //TODO: Create route for opening fahrplan document directly
        //'/fahrplan/:instance/:dutyTitle/:dateYYYYMMDD/otherDoc/:documentId'
        // add userState
        // IMPORTANT: Be sure to set all properties which can be changed and should be watched!!!!!
        f._userState = userStateByFahrdienstFileId[f.id]
          ? userStateByFahrdienstFileId[f.id]
          : {
              favorite: 0,
              dateread: null,
              fileIdFk: f.id,
            };
        f._userState._type = 'fahrplanUserState';

        favoriteService.assertFavoriteIsSet(f);
      });
      favoriteService.addFavoriteStatesFromLocalStorage({ moduleId, items: fahrplanFiles });

      commit('setData', {
        fahrplanFilesById,
        fahrplanAttributes,
        fahrplanFiles,
        userState,
        verkehrsPeriodenKalender,
      });
    } catch (err) {
      console.error(err);
      commit('sync/setSyncState', { moduleId, syncState: SYNC_STATES.FAILURE, msg: err }, ROOT);
      throw err;
    }
  },
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Dispatched in store/sync !!!
   * Gets dispatched after all sync processes have been completed.
   * This is the place to implement any post sync processes.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async onSyncComplete({ commit, dispatch, rootState, rootGetters }) {
    // Implement this method if necessary
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
